import { ClientStorage } from "../../../js/utilities/ClientStorage.js";

/** @type {CuiState} */
const INITIAL_STATE = {
	openState: "collapsed",
	exchanges: [],
};

export class ConversationalUi extends HTMLElement {
	static observedAttributes = ["open-state"];

	static {
		if (!window.customElements.get("conversational-ui")) {
			window.customElements.define("conversational-ui", ConversationalUi);
		}
	}

	#storage = new ClientStorage(window.sessionStorage);
	#toggleButton;
	#form;
	#input;

	constructor() {
		super();
		this.setAttribute("data-js", "true");

		this.#toggleButton = /** @type {HTMLButtonElement} */ (
			this.querySelector(".js-CUI-toggleButton")
		);

		this.#form = /** @type {HTMLFormElement} */ (
			this.querySelector("form[data-drupal-selector='dole-ai-chat']")
		);

		this.#input = /** @type {HTMLInputElement} */ (
			this.#form.querySelector("input[name='query']")
		);

		const triggerButtons = /** @type {NodeListOf<HTMLButtonElement>} */ (
			this.ownerDocument.querySelectorAll(
				"button[data-conversational-ui-trigger]",
			)
		);

		for (const button of triggerButtons) {
			button.addEventListener("click", () => {
				this.openState = "expanded";
			});
		}

		const suggestionButtons = /** @type {NodeListOf<HTMLButtonElement>} */ (
			this.querySelectorAll(".js-CUI-applySuggestionButton")
		);

		for (const button of suggestionButtons) {
			button.addEventListener("click", () => {
				this.#input.value = (button.textContent ?? "").trim();
				this.#input.focus();

				const enterKeyEvent = new KeyboardEvent("keydown", {
					key: "Enter",
					keyCode: 13,
					code: "Enter",
					bubbles: true,
					cancelable: true,
				});

				this.#input.dispatchEvent(enterKeyEvent);
			});
		}
	}

	get openState() {
		return (
			/** @type {CuiState["openState"]} */ (this.getAttribute("open-state")) ??
			INITIAL_STATE.openState
		);
	}

	set openState(state) {
		state = state ?? INITIAL_STATE.openState;
		this.setAttribute("open-state", state);
		this.#toggleButton.setAttribute(
			"aria-expanded",
			String(state === "expanded"),
		);
		this.#updateStoredState();
	}

	connectedCallback() {
		this.#toggleButton.addEventListener("click", this.#toggleOpenState);
		const state = this.#getStoredState();
		this.openState = state.openState;
	}

	disconnectedCallback() {
		this.#toggleButton.removeEventListener("click", this.#toggleOpenState);
	}

	attributeChangedCallback(name, oldValue, newValue) {
		if (name === "open-state" && oldValue !== newValue) {
			this.openState = /** @type {CuiState["openState"]} */ (newValue);
		}
	}

	#toggleOpenState = () => {
		this.openState = this.openState === "collapsed" ? "expanded" : "collapsed";
	};

	#getStoredState() {
		const raw = this.#storage.get("dole-cui-state");
		return raw ? JSON.parse(raw) : INITIAL_STATE;
	}

	#updateStoredState() {
		const state = {
			openState: this.openState === "expanded" ? "collapsed" : this.openState,
			exchanges: [],
		};
		this.#storage.set("dole-cui-state", JSON.stringify(state));
	}
}
